<template>
    <div id="roles" class="setting-page-content-wrapper">
        <template v-if="isEdit">
            <role-edit
                :roleId="roleId"
                @saved="onRoleSaved"
                @cancelled="goBackToRoles"
                ref="roleEditComponent"
            />
        </template>
        <template v-else>
            <div class="roles-page">
                <div class="roles-header">
                    <a-input-search
                        v-model="searchText"
                        :placeholder="$t('message.search')"
                        class="search-input roles-search"
                        @search="onSearch"
                    />
                    <div class="roles-header__right">
                        <SupsisButton small ghost @click="collapseAllRoles">
                            <md-icon>unfold_less</md-icon>
                            {{ $t("message.collapseAll") }}
                        </SupsisButton>
                        <SupsisButton small ghost @click="expandAllRoles">
                            <md-icon>unfold_more</md-icon>
                            {{ $t("message.expandAll") }}
                        </SupsisButton>
                        <SupsisButton ghost @click="createNewRole">
                            <md-icon>add</md-icon>
                            {{ $t("message.createRole") }}
                        </SupsisButton>
                    </div>
                </div>

                <div class="role-groups">
                    <div
                        v-for="(usersInGroup, groupKey) in roleGroups"
                        :key="groupKey"
                        class="role-card"
                        :class="{ 'role-card--no-role': groupKey === noRoleKey }"
                    >
                        <div
                            class="role-card__header"
                            @click="toggleRoleGroup(groupKey)"
                        >
                            <div class="role-card__title-row">
                                <md-icon class="role-card__toggle">{{
                                    expandedRoleGroups.has(groupKey) ? "expand_more" : "chevron_right"
                                }}</md-icon>
                                <span class="role-card__name">{{ getRoleGroupDisplayName(groupKey) }}</span>
                                <span class="role-card__count">{{ usersInGroup.length }}</span>
                            </div>
                            <div class="role-card__actions" @click.stop v-if="getRoleByKey(groupKey)">
                                <a-button
                                    v-if="checkPermission(PERMISSION_TYPES['role.update'].tr.name)"
                                    type="link"
                                    size="small"
                                    class="action-btn"
                                    @click="editRole(getRoleByKey(groupKey))"
                                >
                                    <span class="material-icons">edit</span>
                                </a-button>
                                <a-button
                                    v-if="checkPermission(PERMISSION_TYPES['role.delete'].tr.name)"
                                    type="link"
                                    size="small"
                                    class="action-btn action-btn--danger"
                                    @click="remove(getRoleByKey(groupKey)._id)"
                                >
                                    <ActionDelete />
                                </a-button>
                            </div>
                        </div>

                        <div v-show="expandedRoleGroups.has(groupKey)" class="role-card__body">
                            <div class="role-card__user-actions" v-if="canUpdateUser && groupKey !== noRoleKey">
                                <SupsisButton xsmall ghost @click="openAddUserModal(groupKey)">
                                    <md-icon>person_add</md-icon>
                                    {{ $t("message.addUserToRole") }}
                                </SupsisButton>
                                <SupsisButton
                                    v-if="getRoleGroupUsers(groupKey).length > 0 && !isAllSelectedInRoleGroup(groupKey)"
                                    xsmall
                                    ghost
                                    @click="selectAllInRoleGroup(groupKey)"
                                >
                                    <md-icon>select_all</md-icon>
                                    {{ $t("message.selectAll") }}
                                </SupsisButton>
                                <SupsisButton
                                    v-if="selectedUserIdsInRoleGroup(groupKey).length > 0"
                                    xsmall
                                    ghost
                                    @click="deselectAllInRoleGroup(groupKey)"
                                >
                                    <md-icon>deselect</md-icon>
                                    {{ $t("message.deselectAll") }}
                                </SupsisButton>
                                <a-dropdown
                                    v-if="selectedUserIdsInRoleGroup(groupKey).length > 0"
                                    :trigger="['click']"
                                >
                                    <SupsisButton xsmall ghost>
                                        <md-icon>drive_file_move</md-icon>
                                        {{ $t("message.moveToRole") }} ({{ selectedUserIdsInRoleGroup(groupKey).length }})
                                    </SupsisButton>
                                    <a-menu slot="overlay" @click="({ key }) => moveUsersToRoleDirectly(groupKey, key)">
                                        <a-menu-item
                                            v-for="r in getOtherRoles(groupKey)"
                                            :key="r._id"
                                        >
                                            {{ r.name }}
                                        </a-menu-item>
                                    </a-menu>
                                </a-dropdown>
                            </div>

                            <div class="role-card__user-list">
                                <div
                                    v-for="user in usersInGroup"
                                    :key="user._id"
                                    class="role-user-item"
                                    :class="{ selected: isUserSelectedInRole(user._id) }"
                                >
                                    <a-checkbox
                                        v-if="canUpdateUser && groupKey !== noRoleKey"
                                        :checked="isUserSelectedInRole(user._id)"
                                        @change="toggleRoleUserSelection(user._id)"
                                    />
                                    <AgentAvatar :userID="user._id" :size="32" :isNamed="false" />
                                    <span class="role-user-item__name">{{ user.name || user.email }}</span>
                                    <div class="role-user-item__actions" v-if="canUpdateUser && groupKey !== noRoleKey && getRoleByKey(groupKey)">
                                        <a-dropdown :trigger="['click']">
                                            <a-button type="link" size="small">
                                                <md-icon>more_vert</md-icon>
                                            </a-button>
                                            <a-menu slot="overlay">
                                                <a-menu-item @click="openRemoveUserModal(user, getRoleByKey(groupKey)._id)">
                                                    <md-icon>person_remove</md-icon>
                                                    {{ $t("message.removeUserFromRole") }}
                                                </a-menu-item>
                                                <a-sub-menu :title="$t('message.moveToRole')">
                                                    <a-menu-item
                                                        v-for="r in getOtherRoles(groupKey)"
                                                        :key="r._id"
                                                        @click="moveSingleUserToRole(user, groupKey, r._id)"
                                                    >
                                                        {{ r.name }}
                                                    </a-menu-item>
                                                </a-sub-menu>
                                            </a-menu>
                                        </a-dropdown>
                                    </div>
                                </div>
                                <div v-if="usersInGroup.length === 0" class="role-card__empty">
                                    {{ $t("message.noUsersInRole") }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <a-modal
            :visible="showAddUserModal"
            :title="$t('message.addUserToRole')"
            :footer="null"
            @cancel="closeAddUserModal"
            :width="380"
        >
            <a-select
                v-model="selectedUser"
                :placeholder="$t('message.selectUser')"
                show-search
                option-filter-prop="children"
                style="width: 100%"
                :loading="loadingUsers"
                :filter-option="filterOption"
            >
                <a-select-option
                    v-for="(user, index) in availableUsers"
                    :key="`add-user-${index}`"
                    :value="user._id"
                >
                    {{ user.name || user.email || $t("message.unnamed") }}
                </a-select-option>
            </a-select>
            <div class="modal-footer-actions">
                <SupsisButton ghost @click="closeAddUserModal">{{ $t("message.cancel") }}</SupsisButton>
                <SupsisButton primary :disabled="addUserLoading" @click="handleAddUser">
                    {{ $t("message.save") }}
                </SupsisButton>
            </div>
        </a-modal>

        <a-modal
            :visible="showRemoveUserModal"
            :title="$t('message.removeUserFromRole')"
            :footer="null"
            @cancel="closeRemoveUserModal"
            :width="400"
        >
            <div class="remove-user-modal-content">
                <p>
                    <strong>{{ selectedUser && (selectedUser.name || selectedUser.email || $t("message.unnamed")) }}</strong>
                    {{ $t("message.removeUserFromRoleConfirm") }}
                </p>
                <a-form-item :label="$t('message.selectNewRole')" style="margin-top: 16px;">
                    <a-select
                        v-model="selectedNewRole"
                        :placeholder="$t('message.selectRole')"
                        show-search
                        option-filter-prop="children"
                        style="width: 100%"
                        :filter-option="filterOption"
                    >
                        <a-select-option
                            v-for="(role, index) in roles"
                            :key="`remove-role-${index}`"
                            :value="role._id"
                            :disabled="role._id === selectedRoleId"
                        >
                            {{ role.name }}
                        </a-select-option>
                    </a-select>
                </a-form-item>
                <div class="modal-footer-actions">
                    <SupsisButton ghost @click="closeRemoveUserModal">{{ $t("message.cancel") }}</SupsisButton>
                    <SupsisButton primary :disabled="removeUserLoading" @click="handleRemoveUser">
                        {{ $t("message.continue") }}
                    </SupsisButton>
                </div>
            </div>
        </a-modal>

        <a-modal
            :title="$t('message.bulkUpdateProgress')"
            :visible="bulkProgress.visible"
            :footer="null"
            :closable="!bulkProgress.loading"
            :maskClosable="false"
        >
            <div class="progress-modal">
                <a-progress
                    :percent="bulkProgress.total ? Math.round((bulkProgress.current / bulkProgress.total) * 100) : 0"
                    status="active"
                />
                <p class="progress-modal__text">
                    {{ bulkProgress.current }} / {{ bulkProgress.total }} {{ $t("message.usersUpdated") }}
                </p>
            </div>
        </a-modal>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { log2 } from "../../../../../../utils/log2";
import * as _ from "lodash";
import { PERMISSION_TYPES } from "../../../../../../language/permissionTypes";
import { sendSocketMessage } from "../../../../../../utils/socketStream";
import Pagination from "../../../../../../components/Pagination.vue";
import ActionEdit from "../../../../../../components/svg/ActionEdit.vue";
import ActionDelete from "../../../../../../components/svg/ActionDelete.vue";
import SupsisTable from "../../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../components/SupsisButton.vue";
import CreateButton from "../../../../../../components/CreateButton.vue";
import RoleEdit from "./RoleEdit.vue";
import AgentAvatar from "../../../../../../components/common/AgentAvatar.vue";

library.add(faSpinner);
const log = log2("Roles");

export default {
    name: "Roles",
    components: {
        CreateButton,
        SupsisButton,
        SupsisTable,
        Pagination,
        ActionEdit,
        ActionDelete,
        RoleEdit,
        AgentAvatar,
    },
    props: {
        isEdit: {
            type: Boolean,
            default: false
        },
        roleId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            PERMISSION_TYPES,
            isBusy: true,
            loading: false,
            searchText: undefined,
            roles: [],
            editingRole: null,
            users: [],
            expandedRoleUsers: [],
            usersLoaded: false,
            showAddUserModal: false,
            showRemoveUserModal: false,
            selectedRoleId: null,
            selectedUser: null,
            selectedNewRole: null,
            availableUsers: [],
            loadingUsers: false,
            addUserLoading: false,
            removeUserLoading: false,
            expandedRoleGroups: new Set(),
            selectedUserIds: [],
            moveToRoleLoading: false,
            bulkProgress: { visible: false, current: 0, total: 0, loading: false },
            cols: [
                {
                    field: "name",
                    key: "name",
                    title: this.$t("message.title"),
                },
                {
                    field: "usersSlot",
                    key: "usersSlot",
                    title: "Kullanıcılar",
                    width: 250,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const component = this;
                        const roleId = row._id;
                        const userCount = component.getRoleUserCount(roleId);
                        const isExpanded = component.expandedRoleUsers.includes(roleId);
                        const roleUsers = component.getRoleUsers(roleId);

                        return (
                            <div class="role-users-cell">
                                <div
                                    class="role-users-toggle"
                                    on-click={() => component.toggleRoleUsers(roleId)}
                                >
                                    <div class="role-users-badge">
                                        <span class="user-count">{userCount}</span>
                                        <span class="user-label">Kullanıcı</span>
                                    </div>
                                    <i class={`fas fa-chevron-${isExpanded ? "up" : "down"} toggle-icon`}></i>
                                </div>
                                {isExpanded && (
                                    <div class="role-users-dropdown">
                                        <div class="role-users-actions">
                                            <a-button
                                                type="primary"
                                                size="small"
                                                on-click={() => component.openAddUserModal(roleId)}
                                                class="add-user-btn"
                                            >
                                                <i class="fas fa-plus"></i>
                                                Kullanıcı Ekle
                                            </a-button>
                                        </div>
                                        {roleUsers.length > 0 ? (
                                            <div class="role-users-list">
                                                {roleUsers.map((user) => {
                                                    const userName = user.name || user.email || "İsimsiz";
                                                    const userInitial = userName.charAt(0).toUpperCase();
                                                    return (
                                                        <div key={user._id} class="role-user-item">
                                                            <div class="user-avatar">
                                                                {userInitial}
                                                            </div>
                                                            <a-tooltip title={userName} placement="top">
                                                                <span class="user-name">{userName}</span>
                                                            </a-tooltip>
                                                            <a-button
                                                                type="link"
                                                                size="small"
                                                                danger
                                                                on-click={() => component.openRemoveUserModal(user, roleId)}
                                                                class="remove-user-btn"
                                                            >
                                                                <i class="fas fa-times"></i>
                                                            </a-button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div class="no-users">
                                                <i class="fas fa-users"></i>
                                                <span>Bu rolde kullanıcı bulunmamaktadır</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    },
                },
                {
                    align: "right",
                    field: "actionsSlot",
                    key: "actionsSlot",
                    title: this.$t("message.transactions"),
                    fixed: "right",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const renderEditButton = () => {
                            return (
                                this.checkPermission(PERMISSION_TYPES["role.update"].tr.name) && (
                                    <a-button
                                        class="actionButton d-flex align-items-center"
                                        on-click={() => this.editRole(row)}
                                    >
                                        <span class="material-icons" style="color:#1bc5bd;">edit</span>
                                    </a-button>
                                )
                            );
                        };
                        const renderDeleteButton = () => {
                            return (
                                this.checkPermission(PERMISSION_TYPES["role.delete"].tr.name) && (
                                    <a-button
                                        class="actionButton d-flex align-items-center"
                                        on-click={() => this.remove(row._id)}
                                    >
                                        <ActionDelete />
                                    </a-button>
                                )
                            );
                        };
                        return (
                            <div class="d-flex w-100 justify-content-end">
                                {renderEditButton()}
                                {renderDeleteButton()}
                            </div>
                        );
                    },
                },
            ],
            currentSort: {
                columnKey: "name",
                order: "ascend",
            },
            paginationCursor: {
                total: 0,
                page: 1,
                perPage: 10,
            },
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            permissions: (state) => state.permission.permissions,
            isAdmin: (state) => state.auth.adminUser != null,
            storeUsers: (state) => state.user.users.data,
        }),
        ...mapGetters({
            groupedPermissions: "permission/groupedPermissions",
            isDarkMode: "user/isDarkMode",
            checkPermission: "auth/checkPermission",
        }),
        filteredRoles() {
            return this.searchText
                ? this.roles.filter((x) =>
                      x.name.toLocaleLowerCase("tr-TR").includes(this.searchText.toLocaleLowerCase("tr-TR"))
                  )
                : this.roles;
        },
        total() {
            return this.filteredRoles.length;
        },
        currentPageData() {
            const start = (this.paginationCursor.page - 1) * this.paginationCursor.perPage;
            const end = start + this.paginationCursor.perPage;
            return this.filteredRoles.slice(start, end);
        },
        noRoleKey() {
            return this.$t("message.noRole");
        },
        canUpdateUser() {
            return this.checkPermission(PERMISSION_TYPES["user.update"].tr.name);
        },
        roleGroups() {
            const groups = {};
            const noKey = this.noRoleKey;
            groups[noKey] = [];
            this.filteredRoles.forEach((r) => {
                groups[r.name] = [];
            });
            (this.users || []).forEach((user) => {
                const roleId = user.role && (user.role._id || user.role);
                if (!roleId) {
                    groups[noKey].push(user);
                } else {
                    const role = this.roles.find((r) => r._id === roleId);
                    if (role && groups[role.name]) groups[role.name].push(user);
                }
            });
            return groups;
        },
    },
    methods: {
        ...mapActions({
            getPermissions: "permission/getPermissions",
            getRoles: "role/getRoles",
            getRole: "role/getRole",
            createRole: "role/createRole",
            updateRole: "role/updateRole",
            deleteRole: "role/deleteRole",
            getUsers: "user/getUsers",
            updateUser: "user/updateUser",
        }),
        getRoleByKey(groupKey) {
            if (groupKey === this.noRoleKey) return null;
            return this.roles.find((r) => r.name === groupKey);
        },
        getRoleGroupDisplayName(groupKey) {
            if (groupKey === this.noRoleKey) return this.$t("message.noRole");
            return groupKey;
        },
        toggleRoleGroup(groupKey) {
            const next = new Set(this.expandedRoleGroups);
            if (next.has(groupKey)) next.delete(groupKey);
            else next.add(groupKey);
            this.expandedRoleGroups = next;
            this.$forceUpdate();
        },
        selectedUserIdsInRoleGroup(groupKey) {
            const usersInGroup = this.roleGroups[groupKey] || [];
            const ids = usersInGroup.map((u) => u._id);
            return this.selectedUserIds.filter((id) => ids.indexOf(id) !== -1);
        },
        isUserSelectedInRole(userId) {
            return this.selectedUserIds.indexOf(userId) !== -1;
        },
        toggleRoleUserSelection(userId) {
            const idx = this.selectedUserIds.indexOf(userId);
            if (idx !== -1) this.selectedUserIds.splice(idx, 1);
            else this.selectedUserIds.push(userId);
            this.$forceUpdate();
        },
        getRoleGroupUsers(groupKey) {
            return this.roleGroups[groupKey] || [];
        },
        isAllSelectedInRoleGroup(groupKey) {
            var usersInGroup = this.getRoleGroupUsers(groupKey);
            if (usersInGroup.length === 0) return false;
            var selected = this.selectedUserIdsInRoleGroup(groupKey);
            return selected.length === usersInGroup.length;
        },
        selectAllInRoleGroup(groupKey) {
            var usersInGroup = this.getRoleGroupUsers(groupKey);
            var self = this;
            usersInGroup.forEach(function (u) {
                if (self.selectedUserIds.indexOf(u._id) === -1) self.selectedUserIds.push(u._id);
            });
            this.$forceUpdate();
        },
        deselectAllInRoleGroup(groupKey) {
            var usersInGroup = this.getRoleGroupUsers(groupKey);
            var ids = usersInGroup.map(function (u) { return u._id; });
            this.selectedUserIds = this.selectedUserIds.filter(function (id) { return ids.indexOf(id) === -1; });
            this.$forceUpdate();
        },
        getOtherRoles(groupKey) {
            const r = this.getRoleByKey(groupKey);
            const currentId = r ? r._id : null;
            return this.roles.filter((role) => role._id !== currentId);
        },
        moveUsersToRoleDirectly(groupKey, targetRoleId) {
            var role = this.getRoleByKey(groupKey);
            if (!role || !targetRoleId || !this.canUpdateUser) return;
            var userIds = this.selectedUserIdsInRoleGroup(groupKey).slice();
            if (userIds.length === 0) return;
            this.selectedUserIds = this.selectedUserIds.filter(function (id) { return userIds.indexOf(id) === -1; });
            this.moveToRoleLoading = true;
            var updateFns = userIds.map((uid) => () => {
                var user = this.users.find(function (u) { return u._id === uid; });
                if (!user) return Promise.resolve();
                return this.updateUser({
                    user: Object.assign({}, user, { role: targetRoleId }),
                    siteId: this.site._id,
                });
            });
            this.runBulkUpdates(updateFns, () => {
                this.$toasted.show(this.$t("message.roleUsersUpdated"), { duration: 3000, position: "bottom-center" });
                this.usersLoaded = false;
                this.loadUsers();
                this.moveToRoleLoading = false;
            });
        },
        moveSingleUserToRole(user, groupKey, targetRoleId) {
            if (!targetRoleId || !this.canUpdateUser) return;
            this.updateUser({
                user: Object.assign({}, user, { role: targetRoleId }),
                siteId: this.site._id,
            })
                .then(() => {
                    this.$toasted.show(this.$t("message.roleUsersUpdated"), { duration: 3000, position: "bottom-center" });
                    this.usersLoaded = false;
                    this.loadUsers();
                })
                .catch((err) => {
                    log.error(err);
                    this.$toasted.error(this.$t("message.error"), { duration: 3000, position: "bottom-center" });
                });
        },
        collapseAllRoles() {
            this.expandedRoleGroups = new Set();
            this.$forceUpdate();
        },
        expandAllRoles() {
            this.expandedRoleGroups = new Set(Object.keys(this.roleGroups));
            this.$forceUpdate();
        },
        runBulkUpdates(updateFns, onDone) {
            const total = updateFns.length;
            this.bulkProgress = { visible: true, current: 0, total, loading: true };
            let current = 0;
            const run = (index) => {
                if (index >= updateFns.length) {
                    this.bulkProgress.loading = false;
                    if (onDone) onDone();
                    setTimeout(() => {
                        this.bulkProgress = { visible: false, current: 0, total: 0, loading: false };
                        this.$forceUpdate();
                    }, 500);
                    return Promise.resolve();
                }
                return updateFns[index]()
                    .then(() => {
                        current++;
                        this.bulkProgress.current = current;
                        this.$forceUpdate();
                        return run(index + 1);
                    })
                    .catch((err) => {
                        log.error(err);
                        return run(index + 1);
                    });
            };
            run(0);
        },
        filterOption(input, option) {
            const text = option.componentOptions && option.componentOptions.children && option.componentOptions.children[0];
            const str = (text && text.text) ? text.text : "";
            return str.toLowerCase().indexOf((input || "").toLowerCase()) >= 0;
        },
        toggleRoleUsers(roleId) {
            const index = this.expandedRoleUsers.indexOf(roleId);
            if (index > -1) {
                this.expandedRoleUsers.splice(index, 1);
            } else {
                this.expandedRoleUsers.push(roleId);
            }
        },
        getRoleUsers(roleId) {
            if (!this.users || !Array.isArray(this.users)) {
                return [];
            }
            return this.users.filter((user) => {
                return user && user.role && (user.role._id === roleId || user.role === roleId);
            });
        },
        getRoleUserCount(roleId) {
            return this.getRoleUsers(roleId).length;
        },
        createNewRole() {
            this.$emit('route-change', true, 'new');
        },
        editRole(role) {
            this.editingRole = role;
            this.$emit('route-change', true, role._id);
        },
        goBackToRoles() {
            this.$emit('route-change', false, null);
        },
        onRoleSaved() {
            this.goBackToRoles();
            this.get();
        },
        handlePageChange(cursor) {
            this.paginationCursor.page = cursor.page;
            this.paginationCursor.perPage = cursor.perPage;
            this.get();
        },
        onSearch(value) {
            log("onSearch", value);
            this.searchText = value;
        },
        get(getPermissions = false) {
            this.isBusy = true;
            this.getRoles({ perPage: 9999, page: 1 })
                .then((response) => {
                    this.roles = response.data;
                    this.paginationCursor.total = response.total;
                    this.isBusy = false;
                    this.initExpandedRoleGroups();
                    if (getPermissions) {
                        this.getPermissions({ perPage: 100, page: 1 });
                    }
                })
                .catch((err) => {
                    log.error(err);
                    this.isBusy = false;
                });
        },
        initExpandedRoleGroups() {
            const keys = Object.keys(this.roleGroups);
            if (keys.length && this.expandedRoleGroups.size === 0) {
                this.expandedRoleGroups = new Set(keys);
                this.$forceUpdate();
            }
        },
        loadUsers() {
            if (this.usersLoaded || !this.getUsers) {
                return Promise.resolve();
            }
            return this.getUsers({
                perPage: 9999,
                page: 1,
                query: undefined,
                sortBy: "name",
                sortOrder: "ascend",
                updateStore: true,
            })
                .then((response) => {
                    if (response && response.data) {
                        this.users = Array.isArray(response.data.data) ? response.data.data : [];
                    } else {
                        this.users = [];
                    }
                    this.usersLoaded = true;
                })
                .catch((err) => {
                    log.error("Error loading users:", err);
                    this.users = [];
                    this.usersLoaded = false;
                });
        },
        openAddUserModal(groupKey) {
            const role = this.getRoleByKey(groupKey);
            if (!role) return;
            this.selectedRoleId = role._id;
            this.selectedUser = null;
            this.loadAvailableUsers(role._id);
            this.showAddUserModal = true;
        },
        closeAddUserModal() {
            this.showAddUserModal = false;
            this.selectedRoleId = null;
            this.selectedUser = null;
            this.availableUsers = [];
        },
        openRemoveUserModal(user, roleId) {
            this.selectedUser = user;
            this.selectedRoleId = roleId;
            this.selectedNewRole = null;
            this.showRemoveUserModal = true;
        },
        closeRemoveUserModal() {
            this.showRemoveUserModal = false;
            this.selectedUser = null;
            this.selectedRoleId = null;
            this.selectedNewRole = null;
        },
        loadAvailableUsers(roleId) {
            this.loadingUsers = true;
            this.getUsers({
                perPage: 9999,
                page: 1,
                query: undefined,
                sortBy: "name",
                sortOrder: "ascend",
                updateStore: false,
            })
                .then((response) => {
                    if (response && response.data) {
                        const allUsers = Array.isArray(response.data.data) ? response.data.data : [];
                        // Bu rolde olmayan kullanıcıları filtrele
                        this.availableUsers = allUsers.filter((user) => {
                            const userRoleId = user.role && (user.role._id || user.role);
                            return userRoleId !== roleId;
                        });
                    } else {
                        this.availableUsers = [];
                    }
                })
                .catch((err) => {
                    log.error("Error loading available users:", err);
                    this.availableUsers = [];
                })
                .finally(() => {
                    this.loadingUsers = false;
                });
        },
        handleAddUser() {
            if (!this.canUpdateUser) return;
            if (!this.selectedUser || !this.selectedRoleId) {
                this.$message.warning(this.$t("message.selectUser"));
                return;
            }

            const user = this.availableUsers.find((u) => u._id === this.selectedUser);
            if (!user) {
                this.$message.error(this.$t("message.userNotFound"));
                return;
            }

            const updatedUser = Object.assign({}, user, { role: this.selectedRoleId });
            this.addUserLoading = true;
            this.updateUser({
                user: updatedUser,
                siteId: this.site._id,
            })
                .then(() => {
                    this.$toasted.show(this.$t("message.roleUsersUpdated"), { duration: 3000, position: "bottom-center" });
                    this.usersLoaded = false;
                    this.loadUsers();
                    this.closeAddUserModal();
                })
                .catch((err) => {
                    log.error("Error adding user to role:", err);
                    const errMsg = (err.response && err.response.data && err.response.data.message) || err.message || this.$t("message.error");
                    this.$message.error(errMsg);
                })
                .finally(() => {
                    this.addUserLoading = false;
                });
        },
        handleRemoveUser() {
            if (!this.canUpdateUser) return;
            if (!this.selectedUser || !this.selectedRoleId) {
                this.$message.warning(this.$t("message.selectUser"));
                return;
            }

            if (!this.selectedNewRole) {
                this.$message.warning(this.$t("message.selectNewRole"));
                return;
            }

            const updatedUser = Object.assign({}, this.selectedUser, { role: this.selectedNewRole });
            this.removeUserLoading = true;
            this.updateUser({
                user: updatedUser,
                siteId: this.site._id,
            })
                .then(() => {
                    this.$toasted.show(this.$t("message.roleUsersUpdated"), { duration: 3000, position: "bottom-center" });
                    this.usersLoaded = false;
                    this.loadUsers();
                    this.closeRemoveUserModal();
                })
                .catch((err) => {
                    log.error("Error removing user from role:", err);
                    const errMsg = (err.response && err.response.data && err.response.data.message) || err.message || this.$t("message.error");
                    this.$message.error(errMsg);
                })
                .finally(() => {
                    this.removeUserLoading = false;
                });
        },
        async loadRoleForEdit() {
            if (this.isEdit && this.roleId !== 'new') {
                try {
                    this.editingRole = await this.getRole(this.roleId);
                } catch (error) {
                    log.error(error);
                    this.$toasted.error("Rol yüklenirken bir hata oluştu", {
                        duration: 3000,
                        position: "bottom-center"
                    });
                    this.goBackToRoles();
                }
            } else if (this.isEdit && this.roleId === 'new') {
                this.editingRole = { _id: 'new', name: '', permissions: [] };
            }
        },
        remove(id) {
            this.$dialog
                .confirm(this.$t("message.deletePermissionQuestion"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.deleteRole(id)
                        .then((x) => {
                            this.roles = this.roles.filter((r) => r._id !== id);
                            this.$toasted.show(this.$t("message.roleDeleted"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        })
                        .catch((err) => {
                            log.error(err.response, err.message);
                            const errMsg =
                                err.response && err.response.data && err.response.data.localizedMessages
                                    ? err.response.data.localizedMessages[this.$i18n.locale]
                                    : err.message;
                            this.$toasted.error(errMsg, {
                                duration: 4000,
                                position: "bottom-center",
                            });
                        });
                });
        },
    },
    async mounted() {
        await this.get(true);
        await this.loadUsers();
        this.initExpandedRoleGroups();
        if (this.isEdit) {
            await this.loadRoleForEdit();
        }
    },
    async beforeRouteUpdate(to, from, next) {
        // URL değiştiğinde gerekli veriyi yükle
        next();
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
    watch: {
        isEdit: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.loadRoleForEdit();
                }
            }
        },
        roleId: {
            handler(newVal) {
                if (this.isEdit && newVal) {
                    this.loadRoleForEdit();
                }
            }
        },
        paginationCursor: {
            deep: true,
            handler() {
                if (!this.isEdit) {
                    this.get();
                }
            },
        }
    },
};
</script>

<style lang="scss">
#roles {
    position: relative;
    height: 100%;
}

.roles-page {
    padding: 0 16px;
    height: 100%;
}
.roles-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    .roles-search {
        max-width: 320px;
    }
    &__right {
        display: flex;
        align-items: center;
        gap: 8px;
    }
}
.role-groups {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.role-card {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    overflow: hidden;
    &--no-role .role-card__header {
        background: #fafafa;
        border-left: 4px solid #8c8c8c;
    }
    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        cursor: pointer;
        user-select: none;
        border-left: 4px solid #1bc5bd;
        background: #fff;
        &:hover {
            background: #fafafa;
        }
    }
    &__title-row {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    &__toggle {
        font-size: 24px;
        color: #666;
    }
    &__name {
        font-weight: 600;
        font-size: 15px;
        color: #333;
    }
    &__count {
        background: #e8e8e8;
        padding: 2px 10px;
        border-radius: 12px;
        font-size: 12px;
        color: #666;
    }
    &__actions .action-btn {
        padding: 4px;
        &--danger {
            color: #ff4d4f;
        }
    }
    &__body {
        padding: 12px 16px;
        border-top: 1px solid #e8e8e8;
        background: #fafafa;
    }
    &__user-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 12px;
    }
    &__user-list {
        max-height: 320px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    &__empty {
        color: #8c8c8c;
        font-size: 13px;
        padding: 16px;
        text-align: center;
    }
}
.role-user-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: #fff;
    border-radius: 6px;
    border: 1px solid transparent;
    &.selected {
        border-color: #1bc5bd;
        background: #e6fffb;
    }
    &__name {
        font-size: 13px;
        color: #333;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    &__actions {
        flex-shrink: 0;
    }
}
.modal-footer-actions {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
.progress-modal {
    &__text {
        margin-top: 12px;
        text-align: center;
        color: #666;
    }
}

#roles {
    .page-header {
        display: flex;
        flex-direction: column;
        gap: 10px;
        border-bottom: 1px solid var(--color-border-default);
        padding-bottom: 16px;
        margin-bottom: 16px;

        .title {
            font-size: 18px;
            font-weight: 700;
            color: var(--color-text-strong-default);
        }

        .subtitle {
            font-size: 14px;
            font-weight: 400;
            color: var(--color-text-default);
        }
    }

    .groupTitle {
        border-radius: 6px;
        height: 35px;
        display: flex;
        align-items: center;
        margin-bottom: 5px;
        padding: 4px 6px;
        cursor: pointer;
        justify-content: space-between;
        background-color: rgb(243, 246, 249);

        .md-ripple {
            justify-content: flex-end !important;
        }

        label {
            font-weight: 600;
            margin: 0;
            cursor: pointer;
        }
    }

    .md-button {
        // margin: 4px;
        margin: 0;
        padding: 0 14px;
    }

    .md-disabled {
        pointer-events: none;
    }

    .md-button.md-primary.md-ghost {
        &:hover {
            color: white;
            background-color: var(--md-theme-default-primary);
        }
    }

    .role-users-cell {
        .role-users-toggle {
            display: flex;
            align-items: center;
            justify-content: space-between;
            cursor: pointer;
            padding: 8px 12px;
            border-radius: 8px;
            transition: all 0.2s ease;
            user-select: none;

            &:hover {
                background-color: #f5f5f5;
            }

            .role-users-badge {
                display: flex;
                align-items: center;
                gap: 6px;

                .user-count {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 24px;
                    height: 24px;
                    padding: 0 8px;
                    background: linear-gradient(135deg, #1bc5bd 0%, #0ea5a0 100%);
                    color: white;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 600;
                    box-shadow: 0 2px 4px rgba(27, 197, 189, 0.2);
                }

                .user-label {
                    font-size: 13px;
                    color: #666;
                    font-weight: 500;
                }
            }

            .toggle-icon {
                color: #999;
                font-size: 12px;
                transition: transform 0.2s ease;
            }
        }

        .role-users-dropdown {
            margin-top: 8px;
            padding: 12px;
            background: #fafafa;
            border-radius: 8px;
            border: 1px solid #e8e8e8;
            animation: slideDown 0.2s ease;

            .role-users-actions {
                margin-bottom: 12px;
                padding-bottom: 12px;
                border-bottom: 1px solid #e8e8e8;

                .add-user-btn {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    background: linear-gradient(135deg, #1bc5bd 0%, #0ea5a0 100%);
                    border: none;
                    box-shadow: 0 2px 4px rgba(27, 197, 189, 0.2);

                    &:hover {
                        background: linear-gradient(135deg, #0ea5a0 0%, #0d9488 100%);
                        box-shadow: 0 4px 8px rgba(27, 197, 189, 0.3);
                    }
                }
            }

            .role-users-list {
                max-height: 250px;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                gap: 8px;

                &::-webkit-scrollbar {
                    width: 6px;
                }

                &::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 3px;
                }

                &::-webkit-scrollbar-thumb {
                    background: #ccc;
                    border-radius: 3px;

                    &:hover {
                        background: #999;
                    }
                }

                .role-user-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 8px 10px;
                    background: white;
                    border-radius: 6px;
                    transition: all 0.2s ease;
                    border: 1px solid transparent;
                    position: relative;

                    &:hover {
                        border-color: #1bc5bd;
                        box-shadow: 0 2px 4px rgba(27, 197, 189, 0.1);
                        transform: translateX(2px);

                        .remove-user-btn {
                            opacity: 1;
                        }
                    }

                    .remove-user-btn {
                        margin-left: auto;
                        opacity: 0;
                        transition: opacity 0.2s ease;
                        padding: 4px 8px;
                        color: #ff4d4f;

                        &:hover {
                            color: #ff7875;
                            background-color: #fff1f0;
                        }
                    }

                    .user-avatar {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 32px;
                        height: 32px;
                        border-radius: 50%;
                        background: linear-gradient(135deg, #1bc5bd 0%, #0ea5a0 100%);
                        color: white;
                        font-weight: 600;
                        font-size: 13px;
                        flex-shrink: 0;
                        box-shadow: 0 2px 4px rgba(27, 197, 189, 0.2);
                    }

                    .user-name {
                        font-size: 13px;
                        color: #333;
                        font-weight: 500;
                        width: 80px;
                        max-width: 80px;
                        min-width: 80px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        display: block;
                        cursor: default;
                    }
                }
            }

            .no-users {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 24px;
                color: #999;
                gap: 8px;

                i {
                    font-size: 24px;
                    opacity: 0.5;
                }

                span {
                    font-size: 13px;
                    font-style: italic;
                }
            }
        }
    }

    @keyframes slideDown {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .remove-user-modal-content {
        p {
            margin-bottom: 16px;
            color: #333;

            strong {
                color: #1bc5bd;
            }
        }
    }
}
</style>
